import { useEffect, useMemo } from "react";

import { divIcon, latLngBounds } from "leaflet";
import { MapContainer, Marker, Polyline, TileLayer, useMap } from "react-leaflet";

import "leaflet/dist/leaflet.css";

export type TourStop = {
  city: string;
  venue: string;
  venueUrl?: string;
  date: string;
  time?: string;
  scheduleDateTime?: string;
  coordinates?: [number, number];
  dateStatus?: "past" | "current" | "future";
  dateTimestamp?: number;
  isValidated?: boolean;
};

type TourMapProps = {
  onSelectStop: (index: number) => void;
  onSelectVan: () => void;
  selectedStopIndex: number | null;
  selectedVan: boolean;
  stops: TourStop[];
};

function markerIcon(
  selected: boolean,
  dateStatus: TourStop["dateStatus"],
  isValidated?: boolean,
) {
  const isCurrent = dateStatus === "current";

  return divIcon({
    className: [
      "tour-map-marker",
      dateStatus === "past" ? "tour-map-marker-past" : "",
      dateStatus === "current" ? "tour-map-marker-current" : "",
      dateStatus === "future" || !dateStatus ? "tour-map-marker-future" : "",
      isValidated ? "tour-map-marker-validated" : "",
      selected ? "tour-map-marker-selected" : "",
    ]
      .filter(Boolean)
      .join(" "),
    html: isCurrent
      ? '<span class="tour-map-marker-star" aria-hidden="true">★</span>'
      : '<span class="tour-map-marker-pin"><span></span></span>',
    iconSize: isCurrent ? [34, 34] : [24, 24],
    iconAnchor: isCurrent ? [17, 17] : [12, 12],
  });
}

function vanIcon(selected: boolean) {
  return divIcon({
    className: `tour-van-marker ${selected ? "tour-van-marker-selected" : ""}`,
    html: `
      <svg viewBox="0 0 64 40" aria-hidden="true" focusable="false">
        <path d="M8 15h28l8 8h12v9H8z" />
        <path d="M39 17h7l5 5H44z" />
        <circle cx="20" cy="32" r="5" />
        <circle cx="48" cy="32" r="5" />
      </svg>
    `,
    iconSize: [42, 28],
    iconAnchor: [21, 14],
  });
}

export function TourMap({
  onSelectStop,
  onSelectVan,
  selectedStopIndex,
  selectedVan,
  stops,
}: TourMapProps) {
  const mappedStops = useMemo(
    () =>
      stops
        .map((stop, index) => ({ index, stop }))
        .filter(
          (entry): entry is { index: number; stop: TourStop & { coordinates: [number, number] } } =>
            Boolean(entry.stop.coordinates),
        ),
    [stops],
  );
  const route = useMemo(() => mappedStops.map(({ stop }) => stop.coordinates), [mappedStops]);
  const selectedStop = selectedStopIndex === null ? undefined : stops[selectedStopIndex];
  const vanPosition = useMemo(() => getVanPosition(mappedStops), [mappedStops]);

  return (
    <MapContainer
      className="tour-map"
      center={[46.9, 2.8]}
      zoom={6.4}
      scrollWheelZoom={false}
      aria-label="Carte interactive de la tournée"
      style={{ width: "100%", height: "100%" }}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <FitRouteBounds route={route} />
      <PanToSelectedStop stop={selectedStop} />
      <PanToVan selected={selectedVan} vanPosition={vanPosition} />
      <Polyline className="tour-route" positions={route} />
      {vanPosition && (
        <Marker
          eventHandlers={{
            click: onSelectVan,
          }}
          icon={vanIcon(selectedVan)}
          position={vanPosition}
        />
      )}
      {mappedStops.map(({ index, stop }) => (
        <Marker
          eventHandlers={{
            click: () => onSelectStop(index),
          }}
          icon={markerIcon(selectedStopIndex === index, stop.dateStatus, stop.isValidated)}
          key={`${stop.city}-${stop.venue}-${stop.date}-${index}`}
          position={stop.coordinates}
        />
      ))}
    </MapContainer>
  );
}

function FitRouteBounds({ route }: { route: [number, number][] }) {
  const map = useMap();

  useEffect(() => {
    if (route.length < 2) {
      return;
    }

    map.fitBounds(latLngBounds(route), {
      padding: [28, 28],
      maxZoom: 8,
    });
  }, [map, route]);

  return null;
}

function getVanPosition(
  mappedStops: Array<{ index: number; stop: TourStop & { coordinates: [number, number] } }>,
) {
  if (mappedStops.some(({ stop }) => stop.dateStatus === "current")) {
    return;
  }

  const previousStop = [...mappedStops].reverse().find(({ stop }) => stop.dateStatus === "past");
  const nextStop = mappedStops.find(({ stop }) => stop.dateStatus === "future");

  if (!previousStop || !nextStop) {
    return;
  }

  return midpoint(previousStop.stop.coordinates, nextStop.stop.coordinates);
}

function midpoint(start: [number, number], end: [number, number]): [number, number] {
  return [(start[0] + end[0]) / 2, (start[1] + end[1]) / 2];
}

const SELECTED_CITY_ZOOM = 10;

function PanToSelectedStop({ stop }: { stop?: TourStop }) {
  const map = useMap();

  useEffect(() => {
    if (!stop?.coordinates) {
      return;
    }

    map.flyTo(stop.coordinates, SELECTED_CITY_ZOOM, {
      animate: true,
      duration: 0.75,
    });
  }, [map, stop]);

  return null;
}

function PanToVan({
  selected,
  vanPosition,
}: {
  selected: boolean;
  vanPosition?: [number, number];
}) {
  const map = useMap();

  useEffect(() => {
    if (!selected || !vanPosition) {
      return;
    }

    map.flyTo(vanPosition, SELECTED_CITY_ZOOM, {
      animate: true,
      duration: 0.75,
    });
  }, [map, selected, vanPosition]);

  return null;
}
